import { useState } from 'react';
import { ReactComponent as ExpandSVG } from '../../../assets/expand.svg';
import { ReactComponent as CollapseSVG } from '../../../assets/collapse.svg';
import Contraindications from './Contraindications.js';
import Indications from './Indications.js';

const Medication = ({ med, index, medState }) => {
    const [meds, setMeds] = medState;
    const [self, setSelf] = useState(med);
    const [expanded, setExpanded] = useState(false);
    const [mightExpand, setMightExpand] = useState(false);

    const updateCheckbox = (e) => {
        let updated = { ...self };
        const item = e.target;
        const type = item.dataset.type;
        const checked = e.target.checked;
        const name = item.name.split('-')[0];

        if (checked) {
            updated[type][name].push(item.value);
        } else {
            updated[type][name].splice(updated[type][name].indexOf(item.value), 1);
        }

        setSelf(updated);
    }

    const updateTextbox = (e) => {
        let updated = { ...self };
        const item = e.target;
        const type = item.dataset.type;

        if (type === "generic") {
            updated[item.name] = item.value
            setSelf(updated);
        } else {
            updated[type][item.name] = item.value;
        }
    }

    const deleteSelf = () => {
        if (window.confirm("Are you sure you want to delete this medication?")) {
            meds.splice(index, 1);
            setMeds(meds.slice());
        }
    }

    return (
        <div className="medication">
            <div className="main-area">
                <button type="button" className="delete-bttn" onClick={deleteSelf}>✕</button>
                <h3>{self.label ?? "New Medication"}</h3>

                <input
                    type="text"
                    className="id"
                    id={`${self.id}-id`}
                    placeholder="ID"
                    name="id"
                    onChange={updateTextbox}
                    data-type={"generic"}
                    defaultValue={med.id}
                    />

                <input
                    type="text"
                    className="label"
                    id={`${self.id}-label`}
                    placeholder="Label"
                    name="label"
                    onChange={updateTextbox}
                    data-type={"generic"}
                    defaultValue={med.label}
                    />

                <button
                    type="button"
                    className={`expand-bttn ${expanded ? "expanded" : ""}`}
                    onClick={() => setExpanded(!expanded)}
                    onMouseEnter={() => setMightExpand(true)}
                    onMouseLeave={() => setMightExpand(false)}
                >
                    {(!expanded && mightExpand) || (expanded && !mightExpand) ? <ExpandSVG /> : <CollapseSVG />}
                </button>
            </div>

            <div className={`expanded-area ${expanded ? "expanded" : "collapsed"}`}>
                <div className="generic-data">
                    <Contraindications  med={med} />
                    <Indications        med={med} />
                </div>

                <div className="headings">
                    <p></p>
                    <p>Initial Dosage</p>
                    <p>Repeat Dosage</p>
                    <p>Max/Dosage</p>
                    <p>Max Total Dosage</p>
                    <p>Repeat Interval (minutes)</p>
                    <p>Contraindicated?</p>
                    <p>Repeatable?</p>
                </div>
                <div className="adult-data">
                    <div className="basic-info">
                        <p>Adult:</p>
                        <MedGroup isAdult={true} group={med.adult} />
                    </div>

                    <div className="administration-routes">
                        <p>Routes:</p>
                        <Route route="IV/IO"        isadult={true} />
                        <Route route="IM"           isadult={true} />
                        <Route route="IN"           isadult={true} />
                        <Route route="PO"           isadult={true} />
                        <Route route="Inhalation"   isadult={true} />
                    </div>
                </div>

                <div className="pediatric-data">
                    <div className="basic-info">
                        <p>Pediatric:</p>
                        <MedGroup isAdult={false} group={med.pediatric} />
                    </div>

                    <div className="administration-routes">
                        <p>Routes:</p>
                        <Route route="IV/IO"        isadult={false} />
                        <Route route="IM"           isadult={false} />
                        <Route route="IN"           isadult={false} />
                        <Route route="PO"           isadult={false} />
                        <Route route="Inhalation"   isadult={false} />
                    </div>
                </div>
            </div>
        </div>
    );

    function Route({ route, isadult }) {
        const type = isadult ? "adult" : "pediatric";

        return (
            <label>
                <input
                    type="checkbox"
                    id={`${self.id}-route-${route}-${type}`}
                    onChange={updateCheckbox}
                    data-type={type}
                    name={`routes-${type}`}
                    value={route}
                    defaultChecked={med[type].routes.includes(route)}
                />
                {route}
            </label>
        );
    }

    function MedGroup({ group, isAdult }) {
        const type = isAdult ? "adult" : "pediatric";

        return (
            <>
            <input
                type="text"
                name="initialDosage"
                id={`${self.id}-initialDosage-${type}`}
                placeholder="Initial Dosage"
                data-type={type}
                onChange={updateTextbox}
                defaultValue={group.initialDosage} />

            <input
                type="text"
                name="repeatDosage"
                id={`${self.id}-repeatDosage-${type}`}
                placeholder="Repeat Dosage"
                data-type={type}
                onChange={updateTextbox}
                defaultValue={group.repeatDosage} />

            <input
                type="text"
                name="maxDosage"
                id={`${self.id}-max/Dosage-${type}`}
                placeholder="Max/Dosage"
                data-type={type}
                onChange={updateTextbox}
                defaultValue={group.maxDosage} />

            <input
                type="text"
                name="maxTotalDosage"
                id={`${self.id}-maxTotalDosage-${type}`}
                placeholder="Max Total Dosage"
                data-type={type}
                onChange={updateTextbox}
                defaultValue={group.maxTotalDosage} />

            <input
                type="number"
                name="repeatInterval"
                id={`${self.id}-repeatInterval-${type}`}
                placeholder="Interval (minutes)"
                data-type={type}
                onChange={updateTextbox}
                defaultValue={group.repeatInterval} />

            <input
                type="checkbox"
                id={`${self.id}-contraindicated-${type}`}
                name="contraindicated"
                data-type={type}
                onChange={updateCheckbox}
                defaultChecked={group.contraindicated} />

            <input
                type="checkbox"
                id={`${self.id}-repeatable-${type}`}
                name="repeatable"
                data-type={type}
                onChange={updateCheckbox}
                defaultChecked={group.repeatable} />
            </>
        );
    }
}

export default Medication